// Per-issue hub — create + detail + refinement on one route.
//
//   /projects/$projectId/issues/new   → create mode (iid === null)
//   /projects/$projectId/issues/$iid  → detail/edit mode
//
// The page owns a local *draft* — title, markdown description, business
// outcome, acceptance criteria, priority — seeded from the loaded issue
// (edit) or from empty defaults (create), and mutated by:
//   - direct edits in the inline form (markdown editor, inputs, chips)
//   - Accept clicks on the chat's proposal widgets
//
// In edit mode Save persists via the PATCH route; Discard snaps back to
// the last-loaded values. In create mode Create runs the SSE intake
// (`useCreateIssue`) and, on success, navigates to the freshly-minted
// `/issues/:iid` — where the same page (now in edit mode) carries the
// refinement chat. That landing is why no post-create "bounce" is needed.
//
// The refinement chat is the primary right-column tab so the user can
// iterate without leaving the page; the Loom progression timeline and the
// GitLab activity feed live behind tabs but stay one click away. Two
// timelines coexist because they answer different questions: the Loom one
// is "how is this work moving through our methodology?", the GitLab one is
// "what did people say + do on the issue?".

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

import type {
  DashboardEvent,
  DashboardIssue,
  GateVerdict,
  IssueEditorPayload,
  Priority,
} from "@loom/shared/dashboard";

import { dorReasonFields } from "@loom/shared/issue-body";
import {
  BREAKDOWN_RESIZE_CLIENT_BACKSTOP_MS,
  splitAcceptanceCriteria,
} from "@loom/shared/readiness";

import { BreakdownCard } from "../breakdown/BreakdownCard";
import {
  buildKeepSingleMessage,
  buildPieceElicitationMessage,
} from "../breakdown/breakdownMessage";
import { useBreakdownCard } from "../breakdown/useBreakdownCard";
import { ChatPage } from "../chat/ChatPage";
import { buildReadinessAcceptMessage } from "../chat/render/acceptMessage";
import { ReadinessCard } from "../chat/render/ReadinessCard";
import { unreadUserTurns, useRedOfferLatch } from "../chat/render/loomAnswer";
import {
  HOLD_QUIET_MS,
  HOLD_QUIET_PIN_MS,
  useAnswerHold,
  type HeldBatch,
} from "../chat/render/useAnswerHold";
import { useLatchedEntry } from "../chat/useLatchedEntry";
import { isSessionDead } from "../chat/session-status";
import { useWsSession } from "../chat/useWsSession";
import { composeBatch, questionAnswerParts, type HeldAskAttribution } from "../focus/answer-frame";
import { FIELD_ORDER, useFieldCopy, type ReadinessEntry } from "../focus/copy";
import { useRelativeTime } from "../i18n/format.js";
import { useT, type MessageKey } from "../i18n/index.js";
import { RemountSafeStoreProvider } from "../state/remount-safe-state";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";
// The design sketch. ONE widget across every host that shows one — see its own
// header; the hub takes question mode exactly as the Focus step does.
import { FigmaPreview } from "../focus/figma/FigmaPreview";

import {
  AgentAskPin,
  openAsksFor,
  previewAskFor,
  type OpenAsk,
  type OpenPreviewAsk,
} from "./components/AgentAskPin";
import { Avatar } from "./components/Avatar";
import { HeldAnswersBar } from "./components/HeldAnswersBar";
import { Icon } from "./components/Icon";
import { IssueActivityTimeline } from "./components/IssueActivityTimeline";
import { IssueLinksPanel } from "./components/IssueLinksPanel";
import { IssueTimeline } from "./components/IssueTimeline";
import { LinkedMrsPanel } from "./components/LinkedMrsPanel";
import { MarkdownEditor } from "./components/MarkdownEditor";
import { PRIORITY_META } from "./components/SteerRow";
import { TopBar } from "./components/TopBar";
import {
  buildDorChecks,
  diffDraftPatch,
  draftsEqual,
  normalizeAc,
  parseBody,
  reseedAction,
} from "./issueDraft";
import { useCreateIssue } from "./useCreateIssue";
import { useDashboardEvents } from "./useDashboardEvents";
import { useIssueActivity } from "./useIssueActivity";
import { useIssueDetail } from "./useIssueDetail";
import { useIssueTimeline } from "./useIssueTimeline";
import { useProjectMeta } from "./useProjectMeta";
import { useProjectSession } from "./useProjectSession";
import { STAGE_LABEL_KEY, STAGE_VAR } from "./stage";
import { useUpdateIssue } from "./useUpdateIssue";

const GATE_VAR: Record<GateVerdict, string> = {
  pass: "var(--gate-pass)",
  fail: "var(--gate-fail)",
  "n/a": "var(--ink-subtle)",
};

type SideTab = "refine" | "progression" | "activity";

interface DraftState {
  readonly title: string;
  readonly description: string;
  readonly outcome: string;
  readonly acceptanceCriteria: string[];
  readonly priority: Priority;
}

const emptyDraft = (): DraftState => ({
  title: "",
  description: "",
  outcome: "",
  acceptanceCriteria: [""],
  priority: "normal",
});

const draftFromIssue = (issue: DashboardIssue): DraftState => {
  const parsed = parseBody(issue.description);
  return {
    title: issue.title,
    description: parsed.description,
    outcome: parsed.outcome,
    acceptanceCriteria:
      parsed.acceptanceCriteria.length > 0 ? parsed.acceptanceCriteria : [""],
    priority: issue.priority,
  };
};

const PROGRESS_KEY: Record<string, MessageKey> = {
  propose_links: "dashboard.progress.propose_links",
  dor_check: "dashboard.progress.dor_check",
  creating: "dashboard.progress.creating",
};

interface IssuePageProps {
  readonly projectId: number;
  /** `null` renders the create surface (`/issues/new`). */
  readonly iid: number | null;
  readonly onBackToDashboard: () => void;
  readonly onNavigateToIssue: (iid: number) => void;
  /** Called with the new iid once the create intake succeeds. */
  readonly onCreated: (iid: number) => void;
  readonly onBackToPicker: () => void;
}

export const IssuePage = ({
  projectId,
  iid,
  onBackToDashboard,
  onNavigateToIssue,
  onCreated,
  onBackToPicker,
}: IssuePageProps): JSX.Element => {
  const t = useT();
  const relativeTime = useRelativeTime();
  const fieldCopy = useFieldCopy();
  /** The DoR marker's payload, in words. It is machine text — a German session
   *  read "DoR fehlt – missing: role". An unparseable payload falls back to
   *  itself rather than disappearing. */
  const dorReasonText = (reason: string): string => {
    const fields = dorReasonFields(reason);
    return fields.length === 0
      ? reason
      : fields.map((f) => fieldCopy(f).label).join(", ");
  };
  const gateLabel = (verdict: GateVerdict): string =>
    verdict === "n/a"
      ? "n/a"
      : verdict === "pass"
        ? t("dashboard.issuePage.gate.pass")
        : t("dashboard.issuePage.gate.fail");
  const isNew = iid === null;
  const meta = useProjectMeta(projectId);
  const detail = useIssueDetail(projectId, iid);
  const timeline = useIssueTimeline(projectId, iid);
  const activity = useIssueActivity(projectId, iid);
  const update = useUpdateIssue(projectId);
  const create = useCreateIssue(projectId);
  // THIS issue's session, booted on first interaction — see useProjectSession.
  // It is never a session another issue holds, so the refine rail is this
  // issue's conversation only. In create mode (`iid === null`) it is the tab's
  // pre-warm, and nothing here asks for one.
  const session = useProjectSession(projectId, "issue", iid);

  // The page's *draft* — what the user is editing. Seeded from the server
  // payload (edit) or empty defaults (create); reset on iid change so a
  // navigation away doesn't carry stale draft state into a different
  // issue (or into the create form).
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [baseline, setBaseline] = useState<DraftState | null>(null);
  const [side, setSide] = useState<SideTab>("refine");
  const [saveError, setSaveError] = useState<string | null>(null);
  // When Loom (the embedded agent) writes the issue while the user has
  // unsaved edits, we don't clobber the draft — we stash the fresh server
  // truth here and surface a "review" banner instead.
  const [staleServer, setStaleServer] = useState<DraftState | null>(null);
  // Seed for the embedded refine session, set when the user starts refining.
  const [refineSeed, setRefineSeed] = useState<string | null>(null);
  // Whether the user has started refining *this* issue. Gated on a per-issue
  // flag rather than on session existence: an issue this tab refined before
  // (or the pre-warm the page would claim) already has a session id, and that
  // must not skip the "Start refining" step and its seed. Reset on iid change.
  const [refineStarted, setRefineStarted] = useState(false);

  // IssuePage owns the single live refine session. It opens the WebSocket
  // here — `null` until the user has started refining AND the issue's session
  // is bound, so nothing connects before then — and threads it into the
  // embedded ChatPage via `externalSession`. Owning it here lets the
  // readiness card be pinned in the editor pane (always visible) while the
  // chat stays the scrolling conversation, and keeps the socket alive across
  // side-tab switches instead of tearing down when the chat unmounts.
  const wsSession = useWsSession(refineStarted ? session.sessionId : null);

  // Latest readiness card FOR THIS ISSUE from the live session, rendered
  // pinned at the top of the editor pane. `readiness_card_pending` is
  // re-emitted on every propose_card call (verdicts flip red→green as the user
  // answers), so the last one in the timeline is the current state. Null until
  // a card lands. The iid filter stays load-bearing: a session an older bundle
  // shared across issues can still carry other issues' cards in its timeline —
  // and the latch below would otherwise widen that contamination from "after a
  // replay" to "whenever this page renders".
  const derivedReadiness = useMemo<ReadinessEntry | null>(() => {
    if (iid === null) return null;
    for (let i = wsSession.timeline.length - 1; i >= 0; i -= 1) {
      const entry = wsSession.timeline[i];
      if (entry?.kind === "readiness_card" && entry.issueIid === iid) return entry;
    }
    return null;
  }, [wsSession.timeline, iid]);
  // LATCH the pinned card across the reconnect replay window (the WS attach
  // effect wipes the timeline and replays the full bus on every silent 1006
  // reconnect / idle resume / reauth — the derivation above goes null, the
  // pin gate unmounts the card, and its widget drafts die with it).
  // Monotonic-by-seq with a stale-latch self-heal — see useLatchedEntry.
  // Keyed by (sessionId, iid, resetEpoch) because all three change IN PLACE
  // on this page (issue navigation, session reset re-using the id, and
  // `POST /sessions/:id/reset` rebuilding the bus from seq 0 under the same
  // id); a key mismatch makes the latch invisible, so a card can never leak
  // across issues, sessions, or a reset boundary. A latched acceptToken can
  // be one card stale; the server-side token re-verification (single-use,
  // hash-checked) bounds that to an apply-time error, not a wrong write.
  const readinessEntry = useLatchedEntry(
    derivedReadiness,
    `${session.sessionId ?? ""}:${iid ?? ""}:${wsSession.resetEpoch}`,
  );

  // Typed asks the agent raised for THIS issue that the user has not acted on.
  // Deliberately independent of the readiness card: the channel exists for the
  // window BEFORE a card exists, and a concern outlives an all-green one.
  // `handledAskIds` covers the gap between the user acting and the server's
  // `question_resolved` landing. It lives here, not in the pin, so a replay that
  // re-emits the pending after the pin closed cannot resurrect an answered ask.
  const [handledAskIds, setHandledAskIds] = useState<ReadonlySet<string>>(
    new Set<string>(),
  );
  useEffect(() => {
    setHandledAskIds(new Set<string>());
  }, [iid]);
  const openAsks = useMemo(
    () => openAsksFor(wsSession.timeline, iid, handledAskIds),
    [wsSession.timeline, iid, handledAskIds],
  );
  // "Is this the screen you meant?" — the design sketch, folded out of the SAME
  // timeline by the complement of `openAsksFor`'s subject guard. The hub is a
  // second flow host, so it gets the same widget in the same question mode the
  // Focus step does rather than a text box that puts an approval sentence on the
  // bus (see the guard's own comment).
  const previewAsk = useMemo(
    () => previewAskFor(wsSession.timeline, iid, handledAskIds),
    [wsSession.timeline, iid, handledAskIds],
  );
  // THE HELD BATCH. A click on Loom's answer is the user's answer — and so is a
  // typed Send on a card row or on the pin. If each went out as its own message,
  // every one that landed while the agent was working would kill the running
  // turn: N clicks, N turns. So they are HELD, and go out together as ONE
  // structured `question_answer` frame — the frame Focus flushes, built by the
  // same `questionAnswerParts` — once every open row and ask is addressed and
  // the agent is idle (or the user has been quiet for a while). The echo is
  // stamped with this issue, so the grounding gate's issue scoping sees it.
  //
  // No `scope`: only RefineFlow binds a send-scope, so a scoped entry queued
  // from this host would be dropped at redelivery time (the active scope is
  // null) instead of reaching the agent after a reconnect.
  // What each frame THIS mount flushed carried, by its delivery id — so a red on
  // a field that frame did not touch can stay offered while the agent reads it
  // (see `redSettled`). Mount-local on purpose: a turn this mount did not send
  // (a reload, a rail message, another tab) is one it cannot vouch for.
  const ownFlushesRef = useRef<Map<string, { readonly keys: ReadonlySet<string>; readonly freeText: boolean }>>(
    new Map(),
  );
  const onFlushHeld = (batch: HeldBatch): string => {
    if (iid === null) return "";
    const heldText = new Map<string, string>();
    const attributed = new Map<string, HeldAskAttribution>();
    for (const e of batch.entries) {
      heldText.set(e.key, e.text);
      if (e.askId !== undefined) {
        attributed.set(e.key, { askId: e.askId, field: e.field, kind: e.kind ?? "question" });
      }
    }
    const parts = questionAnswerParts(heldText, attributed);
    const clientMsgId = wsSession.sendQuestionAnswer(
      iid,
      composeBatch(heldText, batch.freeText),
      parts.answers,
      batch.declined.map((d) => d.askId),
      {
        ...(parts.cardAnswers.length > 0 ? { cardAnswers: parts.cardAnswers } : {}),
        ...(batch.freeText !== undefined ? { freeText: batch.freeText } : {}),
        answeredCount: batch.entries.length + (batch.freeText !== undefined ? 1 : 0),
      },
    );
    if (clientMsgId !== "") {
      ownFlushesRef.current.set(clientMsgId, {
        keys: new Set(batch.entries.flatMap((e) => (e.field !== null ? [e.field] : []))),
        freeText: batch.freeText !== undefined,
      });
    }
    return clientMsgId;
  };
  // Rows the card still asks the user about: an open question, or a red. A
  // skipped optional field is decided, not open.
  const cardOpenFields = useMemo(
    () =>
      (readinessEntry?.verdicts ?? [])
        .filter(
          (v) =>
            (v.verdict === "needs_input" || v.verdict === "red") &&
            !(v.declined === true),
        )
        .map((v) => v.field),
    [readinessEntry],
  );
  const cardVerdictOf = (field: string | null) =>
    field === null ? undefined : readinessEntry?.verdicts.find((v) => v.field === field);
  const appliedHere =
    wsSession.applyResult !== null &&
    wsSession.applyResult.issueIid === iid &&
    wsSession.applyResult.ok;
  // `busyKnown`: idle is only a fact once the server's snapshot has landed on
  // this socket — before it, `agentBusy` reads false on a first load, and a batch
  // restored from sessionStorage would go out into a running turn and kill it.
  const holdCanFlush =
    refineStarted &&
    wsSession.status === "open" &&
    wsSession.busyKnown &&
    !isSessionDead(wsSession.status);
  const hold = useAnswerHold({
    storageKey: iid !== null ? `loom.issue.held:${projectId}:${iid}` : null,
    sessionId: refineStarted ? session.sessionId : null,
    resetEpoch: wsSession.resetEpoch,
    canFlush: holdCanFlush,
    agentBusy: wsSession.agentBusy,
    // Drained: no ask left on the pin, and every open card row either held or
    // already sent (waiting on Loom). With something still to answer, the
    // batch waits for the user — up to the quiet window.
    openKeys: cardOpenFields,
    openElsewhere: previewAsk !== null || openAsks.length > 0,
    // The pin puts its questions ONE at a time, so each next one needs reading
    // before it can be answered: a longer quiet while it still has any, or the
    // first answer went out alone every time (#247, #248, #258).
    quietMs: previewAsk !== null || openAsks.length > 0 ? HOLD_QUIET_PIN_MS : HOLD_QUIET_MS,
    groundedThroughSeq: readinessEntry?.groundedThroughSeq,
    ackedIds: wsSession.ackedIds,
    // Nobody is waiting for these any more: the card was applied, the ask was
    // resolved server-side, or — for a card row no ask is waiting on — the field
    // went green by another path.
    //
    // An entry that answers an ASK is stale only when that ask is resolved: the
    // field it names being green says nothing about it. A concern is bound to
    // the green field it is ABOUT, and so is the plain "replacement" ask the
    // directive prescribes after one; dropping those on "green" swallowed the
    // user's reply — the pin went, no bar showed, nothing was sent, and the
    // agent waited on an ask the user thought they had answered.
    stale: (e) => {
      if (appliedHere) return true;
      if (e.askId !== undefined) {
        return wsSession.timeline.some(
          (x) => x.kind === "agent_ask" && x.askId === e.askId && x.resolved,
        );
      }
      return cardVerdictOf(e.field)?.verdict === "green";
    },
    onFlush: onFlushHeld,
  });
  // A remount restores the held batch (sessionStorage) but not `handledAskIds`
  // (page state): without this the pin asked AGAIN a question whose answer the
  // bar already said it held. An undo takes the ask out of both in one commit,
  // so this never re-hides an ask the user reopened.
  useEffect(() => {
    const ids = [
      ...[...hold.held.values()].flatMap((e) => (e.askId !== undefined ? [e.askId] : [])),
      ...hold.declined.keys(),
    ];
    if (ids.length === 0) return;
    setHandledAskIds((s) => (ids.every((id) => s.has(id)) ? s : new Set([...s, ...ids])));
  }, [hold.held, hold.declined]);
  // Which card rows the hold holds, by field — the card shows them muted.
  const heldByField = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of hold.held.values()) {
      if (e.field !== null && e.key === e.field) m.set(e.field, e.text);
    }
    return m;
  }, [hold.held]);
  // SETTLED, per field, for a red row's value to be offered as Loom's wording:
  // the agent is idle, nothing from this page is still in flight, and — for a
  // field this page answered — the card has READ that answer (its stamp at or
  // past the echo's ack seq). Idle alone is not enough: a turn that was killed,
  // or that ended without a card, leaves the pre-answer red on screen, and one
  // click on it would replace the user's own words with Loom's. Latched per
  // (field, value) so the offer does not blink away when the next turn starts.
  //
  // Idle is a FACT only on an open socket whose busy state the server has told
  // this socket (`busyKnown`): `agentBusy` reads false on a first load before
  // the snapshot lands, and the latch below would then hold the offer through
  // the turn that follows.
  //
  // And "the card has read it" is read off the TIMELINE, which a reload
  // rebuilds: a user turn newer than the card's stamp BLOCKS the offer (and
  // releases the latch) — unless it is a frame this mount flushed that did not
  // carry this field, which is the one turn known not to have answered it.
  // `answeredAt` is mount-local and alone forgot, on a reload, that the page had
  // answered at all.
  const redOfferLatch = useRedOfferLatch();
  const unreadTurns = useMemo(
    () => unreadUserTurns(wsSession.timeline, readinessEntry, iid),
    [wsSession.timeline, readinessEntry, iid],
  );
  const redSettled = (field: string): boolean => {
    const v = cardVerdictOf(field);
    const blocked = unreadTurns.some((turn) => {
      const own =
        turn.clientMsgId !== undefined ? ownFlushesRef.current.get(turn.clientMsgId) : undefined;
      return own === undefined || own.freeText || own.keys.has(field);
    });
    let now =
      wsSession.status === "open" &&
      wsSession.busyKnown &&
      !wsSession.agentBusy &&
      hold.sent.size === 0 &&
      !blocked;
    const answeredId = hold.answeredAt.get(field);
    if (now && answeredId !== undefined) {
      const ack = answeredId !== "" ? (wsSession.ackedIds.get(answeredId) ?? null) : null;
      const stamp = readinessEntry?.groundedThroughSeq;
      now = ack !== null && stamp !== undefined && stamp >= ack;
    }
    return redOfferLatch(
      field,
      v?.verdict,
      typeof v?.value === "string" ? v.value : undefined,
      now,
      blocked,
    );
  };
  // A card row answered: held, and attributed to an open pin ask for the same
  // field when there is one (the twin — otherwise that ask would sit open and
  // the batch would never drain).
  const answerRow = (field: string, text: string): void => {
    const twin = openAsks.find((a) => !a.concern && a.field === field);
    if (twin !== undefined) setHandledAskIds((s) => new Set(s).add(twin.askId));
    hold.add({
      key: field,
      field,
      ...(twin !== undefined ? { askId: twin.askId, kind: "question" as const } : {}),
      text,
    });
  };
  const undoRow = (field: string): void => {
    const entry = hold.held.get(field);
    if (entry?.askId !== undefined) {
      const askId = entry.askId;
      setHandledAskIds((s) => {
        const n = new Set(s);
        n.delete(askId);
        return n;
      });
    }
    hold.undo(field);
  };
  // A pin answer: held with its attribution. A spec-bound plain ask is keyed by
  // its FIELD, so the card row for that field shows it held too and the two
  // cannot both be answered into one batch; everything else keys on its id.
  const answerAsk = (ask: OpenAsk, text: string): void => {
    if (iid === null) return;
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    const key =
      ask.field !== null && !ask.concern && (FIELD_ORDER as ReadonlyArray<string>).includes(ask.field)
        ? ask.field
        : `ask:${ask.askId}`;
    hold.add({
      key,
      field: ask.field,
      askId: ask.askId,
      kind: ask.concern ? "concern" : "question",
      text,
    });
  };
  /**
   * The sketch verdict: yes or no, and NOTHING else on the wire.
   *
   * Deliberately NOT `sendQuestionAnswer`. That frame carries text, and
   * `stream.ts` echoes a non-empty `text` onto the bus as a `user_message` —
   * so an approval sentence would become matchable by the readiness gate's
   * word-boundary-free quote scan. `figma_preview_verdict` carries no text
   * field at all (`ws/serialize.ts` makes it unrepresentable), writes no
   * `FieldState` and no `greenAtSeq`, and means exactly "Loom read the screen I
   * meant". Approving a sketch grounds nothing.
   */
  const answerPreview = (ask: OpenPreviewAsk, verdict: "right" | "wrong"): void => {
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    wsSession.sendFigmaPreviewVerdict(ask.previewId, ask.askId, verdict);
  };
  /** "Carry on" — the sketch is optional by construction, so this is the
   *  ordinary decline path, and the one control offered when the drawing cannot
   *  be built at all. */
  const skipPreview = (ask: OpenPreviewAsk): void => {
    if (iid === null) return;
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    wsSession.sendQuestionAnswer(iid, "", [], [ask.askId], { answeredCount: 0 });
  };
  // Decline-only batch: no text, one closed ask. The server routes it as an
  // explicit "don't wait for this" — without that routing a skipped ask is a
  // permanent park, since the breakdown deadline is cleared while an ask is open.
  const skipAsk = (ask: OpenAsk): void => {
    if (iid === null) return;
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    // Held like an answer: it rides the same frame as the rest of the batch.
    hold.decline({ askId: ask.askId, field: ask.field });
  };

  // Sizing + breakdown tail (shared derivation with RefineFlow). IssuePage has
  // no plan-validation phase of its own, so the hook auto-kicks the plan+sizing
  // turn at the first allGreen; the split is then OFFERED via the banner below
  // and reviewed in the pinned BreakdownCard. Confirm re-reads the CURRENT
  // acceptToken from the latched readiness entry (never cached).
  // Current acceptance-criteria count → sizing staleness (an edit re-sizes; the
  // autoKick re-fires because a stale sizing reads as unknown).
  const currentAcCount = useMemo(() => {
    const v = readinessEntry?.verdicts.find(
      (x) => x.field === "acceptance_criteria",
    );
    return readinessEntry?.allGreen && typeof v?.value === "string"
      ? splitAcceptanceCriteria(v.value).length
      : 0;
  }, [readinessEntry]);
  const breakdownCard = useBreakdownCard(wsSession, session.sessionId, iid, {
    autoKickPlan: true,
    allGreen:
      refineStarted &&
      readinessEntry?.allGreen === true &&
      readinessEntry.acceptToken !== undefined,
    currentCriteriaCount: currentAcCount,
  });
  // Local (page-lifetime) split-offer state: `bdRequested` shows the drafting
  // hint between click and proposal; `bdDismissed` hides the offer after
  // "keep as one ticket". Both reset on issue navigation.
  const [bdRequested, setBdRequested] = useState(false);
  const [bdDismissed, setBdDismissed] = useState(false);
  useEffect(() => {
    setBdRequested(false);
    setBdDismissed(false);
  }, [iid]);
  // Deadline backstop for a LOST readiness_breakdown_unavailable (the server
  // degrade is in-memory, not persisted — a reconnect window can eat it):
  // return the offer buttons instead of stranding the "drafting…" hint.
  //
  // ABOVE the server's split ceilings (360 s / 600 s, `tail-timer.ts`), the
  // one RefineFlow uses. It was 135 s — below them, so a split the server was
  // still drafting could get its buttons back mid-turn, and a second click
  // starts a second, overlapping turn.
  useEffect(() => {
    if (!bdRequested || breakdownCard.entry !== null) return;
    const timer = setTimeout(
      () => setBdRequested(false),
      BREAKDOWN_RESIZE_CLIENT_BACKSTOP_MS,
    );
    return () => clearTimeout(timer);
  }, [bdRequested, breakdownCard.entry]);

  const draftRef = useRef<DraftState | null>(null);
  draftRef.current = draft;
  const dirtyRef = useRef(false);

  // Reset the per-issue refine gate when navigating between issues.
  useEffect(() => {
    setRefineStarted(false);
    setRefineSeed(null);
  }, [iid]);

  useEffect(() => {
    // Create mode: seed an empty draft once. The detail fetch is a no-op
    // (null iid), so this is the only seed source.
    if (isNew) {
      const next = emptyDraft();
      setDraft(next);
      setBaseline(next);
      setSaveError(null);
      return;
    }
  }, [isNew, iid]);

  useEffect(() => {
    if (!detail.data) return;
    const serverDraft = draftFromIssue(detail.data.issue);
    // Adopt the server payload on initial load or when the draft is clean;
    // stash it behind the review banner when the user has unsaved edits.
    // This is the refetch-after-agent-write dirty-guard.
    if (reseedAction(draftRef.current !== null, dirtyRef.current) === "adopt") {
      setDraft(serverDraft);
      setBaseline(serverDraft);
      setStaleServer(null);
      setSaveError(null);
    } else {
      setStaleServer(serverDraft);
    }
  }, [detail.data]);

  const setField = useCallback(
    <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
      setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
    },
    [],
  );

  const setAcAt = (idx: number, value: string): void =>
    setDraft((cur) =>
      cur
        ? {
            ...cur,
            acceptanceCriteria: cur.acceptanceCriteria.map((a, i) =>
              i === idx ? value : a,
            ),
          }
        : cur,
    );
  const addAc = (): void =>
    setDraft((cur) =>
      cur ? { ...cur, acceptanceCriteria: [...cur.acceptanceCriteria, ""] } : cur,
    );
  const removeAc = (idx: number): void =>
    setDraft((cur) =>
      cur
        ? {
            ...cur,
            acceptanceCriteria:
              cur.acceptanceCriteria.length > 1
                ? cur.acceptanceCriteria.filter((_, i) => i !== idx)
                : cur.acceptanceCriteria,
          }
        : cur,
    );

  const dirty = useMemo(
    () => (draft && baseline ? !draftsEqual(draft, baseline) : false),
    [draft, baseline],
  );
  dirtyRef.current = dirty;

  const issue: DashboardIssue | null = detail.data?.issue ?? null;

  // Document sync (Channel B): when the agent (or any writer) mutates this
  // issue, the server publishes a dashboard event. We refetch the document
  // + timelines; the reseed effect's dirty-guard decides adopt-vs-banner.
  // Filter to this iid; accept iid-less events (the bridge stream-tap
  // coarse fallback can't say which issue it touched). No subscription
  // work in create mode (iid === null).
  const refetchAll = useCallback(() => {
    detail.refetch();
    timeline.refetch();
    activity.refetch();
  }, [detail, timeline, activity]);
  const eventHandlers = useMemo(
    () => ({
      refetch: [refetchAll],
      filter: (e: DashboardEvent): boolean =>
        iid !== null && (e.iid === undefined || e.iid === iid),
    }),
    [refetchAll, iid],
  );
  useDashboardEvents(projectId, eventHandlers);

  // Boot this issue's session (if it has none yet) and seed it with the
  // issue's refine context. The PM directive turns "refine #N" into a
  // grounded, codebase-aware refinement turn.
  const startRefine = useCallback((): void => {
    const cur = detail.data?.issue;
    if (!cur) return;
    setRefineSeed(
      `Let's refine issue #${cur.iid}: "${cur.title}". Ground in the ` +
        `codebase first, then ask me the questions you need to sharpen the ` +
        `scope, business outcome, and acceptance criteria. Apply changes to ` +
        `the issue once we agree.`,
    );
    setRefineStarted(true);
    session.ensure();
  }, [detail.data, session]);

  // Client DoR meter for the create surface (the server rubric is
  // authoritative at save time; this is fast local feedback). Edit mode
  // shows the server-authoritative DoR chip in the header instead.
  const dorChecks = useMemo(
    () =>
      draft
        ? buildDorChecks(
            draft.title,
            draft.description,
            draft.outcome,
            draft.acceptanceCriteria,
          )
        : [],
    [draft],
  );
  const dorPassed = dorChecks.filter((c) => c.ok).length;
  const dorPct =
    dorChecks.length > 0 ? Math.round((dorPassed / dorChecks.length) * 100) : 0;
  const dorReady = dorChecks.length > 0 && dorPassed === dorChecks.length;

  const buildPayload = (cur: DraftState): IssueEditorPayload => ({
    title: cur.title.trim() || "Untitled request",
    description: cur.description,
    outcome: cur.outcome,
    acceptanceCriteria: normalizeAc(cur.acceptanceCriteria),
    priority: cur.priority,
  });

  const onSave = async (): Promise<void> => {
    if (!draft || !issue || !baseline) return;
    setSaveError(null);
    // Only what changed. The body trio travels only when one of its three
    // fields did, so a title fix never rewrites the ticket body — see
    // `diffDraftPatch`.
    const patch = diffDraftPatch(draft, baseline);
    if (patch === null) {
      // Dirty but nothing to send: the one difference is a blank title, which
      // never replaces a real one. Put the title back instead of leaving a Save
      // button that does nothing.
      setDraft((cur) => (cur ? { ...cur, title: baseline.title } : cur));
      return;
    }
    try {
      await update.update(issue.iid, patch);
      // Mark the saved values as the new baseline so the refetch that
      // follows re-seeds cleanly (dirty=false) instead of tripping the
      // stale-server banner against our own write.
      setBaseline(draft);
      setStaleServer(null);
      detail.refetch();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  };

  const onCreate = async (): Promise<void> => {
    if (!draft) return;
    setSaveError(null);
    try {
      // Link proposals + the complexity/milestone nudge are intentionally
      // deferred to the in-page agent rail (Phase 3) and the structured
      // proposal card (Phase 6); create posts with no accepted links.
      const result = await create.submit(buildPayload(draft), []);
      onCreated(result.issue.iid);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  };

  const onDiscard = (): void => {
    if (baseline) setDraft(baseline);
    setSaveError(null);
  };

  const headerChips = useMemo(() => {
    if (!issue) return null;
    return (
      <div className="issue-page-chips">
        <span
          className="issue-page-chip issue-page-stage"
          style={{ background: STAGE_VAR[issue.stage] }}
        >
          {t(STAGE_LABEL_KEY[issue.stage])}
        </span>
        <span
          className="issue-page-chip issue-page-gate"
          style={{ borderColor: GATE_VAR[issue.dor] }}
          title={
            issue.dor === "fail" && issue.dorReason
              ? t("dashboard.issuePage.dorFailTitle", {
                  reason: dorReasonText(issue.dorReason),
                })
              : t("dashboard.issuePage.dorTitle", { verdict: gateLabel(issue.dor) })
          }
        >
          {t("dashboard.issuePage.dorChip", { verdict: gateLabel(issue.dor) })}
        </span>
        <span
          className="issue-page-chip issue-page-gate"
          style={{ borderColor: GATE_VAR[issue.dod] }}
          title={t("dashboard.issuePage.dodTitle", { verdict: gateLabel(issue.dod) })}
        >
          {t("dashboard.issuePage.dodChip", { verdict: gateLabel(issue.dod) })}
        </span>
        {issue.points !== null && (
          <span className="issue-page-chip issue-page-points">
            {t("dashboard.issuePage.pt", { points: issue.points })}
          </span>
        )}
      </div>
    );
  }, [issue, t]);

  const canCreate = !!draft && draft.title.trim().length > 0 && !create.saving;
  const railActive =
    !isNew && side === "refine" && refineStarted && session.sessionId !== null;

  return (
    // The remount-safe store rides the WS hook (it survives reconnects AND the
    // detail-refetch null window that unmounts the whole grid below — see
    // useIssueDetail's setData(null)-on-refetch). The pinned ReadinessCard and
    // the embedded ChatPage both re-seed their widget drafts / one-shot
    // latches from it; ChatPage's own provider resolves to this same store via
    // `externalSession`, so the two surfaces share one bag.
    <RemountSafeStoreProvider store={wsSession.uiState}>
    <div className="loom-dashboard">
      {/* No `health`: this page never fetches the project summary that carries
          it, so the pill was permanently "Checking…". Absent, not null — null
          means a check is in flight. */}
      <TopBar project={meta.data} onSwitchProject={onBackToPicker} />
      <div className={`lpage issue-page${railActive ? " has-chat" : ""}`}>
        <button
          type="button"
          className="issue-page-back"
          onClick={onBackToDashboard}
        >
          <Icon name="chevron-down" size={12} /> {t("dashboard.issuePage.backToDashboard")}
        </button>

        {!isNew && detail.loading && <p className="muted">{t("dashboard.issuePage.loadingIssue")}</p>}
        {!isNew && detail.error && !detail.loading && (
          <p className="form-error">{detail.error}</p>
        )}

        {(isNew ? draft !== null : issue && draft) && draft && (
          <>
            {isNew ? (
              <header className="issue-page-header">
                <div className="issue-page-title-row">
                  <span className="issue-page-key">{t("dashboard.issuePage.new")}</span>
                  <h1 className="issue-page-title">{t("dashboard.issuePage.tellLoom")}</h1>
                </div>
                <div className="issue-page-meta">
                  <span className="issue-page-meta-text">
                    {t("dashboard.issuePage.fileRough")}
                  </span>
                  <div className="issue-page-save-row">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={onBackToDashboard}
                      disabled={create.saving}
                    >
                      {t("dashboard.issuePage.cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => void onCreate()}
                      disabled={!canCreate}
                    >
                      <Icon name="check" size={12} />
                      {create.saving ? t("dashboard.issuePage.creating") : t("dashboard.issuePage.createRequest")}
                    </button>
                  </div>
                </div>
                {create.saving && create.progress && (
                  <div className="issue-page-dor-reason">
                    <Icon name="spark" size={12} />
                    {/* The server's `progress.message` is NOT rendered: it is
                        workflow-authored developer English ("Checking required
                        slots") and never translated. The phase is the honest
                        localizable fact. */}
                    <span>
                      {PROGRESS_KEY[create.progress.phase]
                        ? t(PROGRESS_KEY[create.progress.phase]!)
                        : t("dashboard.progress.working")}
                    </span>
                  </div>
                )}
                {saveError && (
                  <div className="issue-page-dor-reason" role="alert">
                    <Icon name="flag" size={12} />
                    <span>{saveError}</span>
                  </div>
                )}
              </header>
            ) : (
              issue && (
                <header className="issue-page-header">
                  {detail.data?.epicParent && (
                    <button
                      type="button"
                      className="issue-epic-crumb"
                      onClick={() => onNavigateToIssue(detail.data!.epicParent!.iid)}
                    >
                      <span className="issue-epic-crumb-glyph" aria-hidden />
                      {t("breakdown.epicCrumb", {
                        iid: String(detail.data.epicParent.iid),
                        title: detail.data.epicParent.title,
                      })}
                    </button>
                  )}
                  {/* Parent-epic identity: on a return visit a split parent is
                      otherwise indistinguishable from a normal issue. Say it's
                      tracking-only and point at the children (the Sub-issues
                      group in the links panel below). */}
                  {detail.data?.epicChildIids !== undefined &&
                    detail.data.epicChildIids.length > 0 && (
                      <div className="issue-epic-banner">
                        <span className="issue-epic-banner-glyph" aria-hidden />
                        {t("breakdown.epicBanner", {
                          count: String(detail.data.epicChildIids.length),
                        })}
                      </div>
                    )}
                  <div className="issue-page-title-row">
                    <span className="issue-page-key">{issue.key}</span>
                    <h1 className="issue-page-title">
                      {draft.title || issue.title}
                    </h1>
                  </div>
                  <div className="issue-page-meta">
                    <Avatar handle={issue.assignee} size={20} />
                    <span className="issue-page-meta-text">
                      {issue.assignee ? `@${issue.assignee}` : t("dashboard.issuePage.unassigned")}{" "}
                      ·{" "}
                      {/* From the ISO instants, not the server's `age` /
                          `updated` — those are rendered English ("today",
                          "2s"), so a German session read "erstellt today ·
                          aktualisiert 2s". */}
                      {t("dashboard.issuePage.createdUpdated", {
                        age: relativeTime(issue.createdAt),
                        updated: relativeTime(issue.updatedAt),
                      })}
                    </span>
                    <a
                      className="issue-page-meta-text issue-page-gitlab"
                      href={issue.webUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("dashboard.issuePage.openIssue")}
                    </a>
                    <div className="issue-page-save-row">
                      {dirty && (
                        <span className="issue-page-dirty-pill">
                          {t("dashboard.issuePage.unsavedChanges")}
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={onDiscard}
                        disabled={!dirty || update.saving}
                      >
                        {t("dashboard.issuePage.discard")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => void onSave()}
                        disabled={!dirty || update.saving}
                      >
                        <Icon name="check" size={12} />
                        {update.saving ? t("dashboard.issuePage.saving") : t("dashboard.issuePage.save")}
                      </button>
                    </div>
                  </div>
                  {headerChips}
                  {saveError && (
                    <div className="issue-page-dor-reason" role="alert">
                      <Icon name="flag" size={12} />
                      <span>{saveError}</span>
                    </div>
                  )}
                  {issue.dor === "fail" && issue.dorReason && (
                    <div className="issue-page-dor-reason">
                      <Icon name="flag" size={12} />
                      <span>
                        {t("dashboard.issuePage.dorMissing", {
                          reason: dorReasonText(issue.dorReason),
                        })}
                      </span>
                    </div>
                  )}
                  {staleServer && (
                    <div className="issue-page-dor-reason" role="alert">
                      <Icon name="spark" size={12} />
                      <span>
                        {t("dashboard.issuePage.loomUpdated")}
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setDraft(staleServer);
                          setBaseline(staleServer);
                          setStaleServer(null);
                        }}
                      >
                        {t("dashboard.issuePage.reviewChanges")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setStaleServer(null)}
                      >
                        {t("dashboard.issuePage.keepMine")}
                      </button>
                    </div>
                  )}
                </header>
              )
            )}

            <div className="issue-page-grid">
              <div className="issue-page-main">
                {/* A typed ask outranks the card, as it does in the Focus
                    ladder: the agent is BLOCKED on this answer, and a concern it
                    raised about an already-green field would otherwise be read
                    as noise next to a card saying everything is verified. Same
                    dead-session gate as the card below — an answer queued onto a
                    socket that can never flush is worse than no control. */}
                {/* THE SKETCH, above the pin and above the card — the same
                    precedence the Focus ladder gives it, for the same reason:
                    it asks whether Loom is looking at the right design, which is
                    upstream of every question that design informs. While it is
                    open it is the ONLY question on screen, so the pin below waits
                    its turn instead of competing with it. */}
                {!isNew &&
                  refineStarted &&
                  !isSessionDead(wsSession.status) &&
                  previewAsk !== null && (
                    <div className="issue-page-figma-preview">
                      <FigmaPreview
                        sessionId={session.sessionId}
                        previewId={previewAsk.previewId}
                        // This chat's own copy of the picture — see the same
                        // call in `RefineFlow`. Null keeps the route.
                        scene={wsSession.figmaSceneFor(previewAsk.previewId)}
                        askId={previewAsk.askId}
                        onVerdict={(verdict) => answerPreview(previewAsk, verdict)}
                        onSkip={() => skipPreview(previewAsk)}
                      />
                    </div>
                  )}
                {/* What is HELD — card rows and pin answers alike — above both,
                    so a batch waiting on Loom is never invisible. */}
                {!isNew && refineStarted && !isSessionDead(wsSession.status) && (
                  <HeldAnswersBar
                    count={hold.count}
                    agentBusy={wsSession.agentBusy}
                    canSend={holdCanFlush}
                    onSendNow={() => {
                      hold.flushNow();
                    }}
                  />
                )}
                {!isNew &&
                  refineStarted &&
                  !isSessionDead(wsSession.status) &&
                  previewAsk === null &&
                  openAsks.length > 0 && (
                    <AgentAskPin
                      asks={openAsks}
                      projectId={projectId}
                      onAnswer={answerAsk}
                      onSkip={skipAsk}
                      // A concern shows what the field says now — the card's
                      // value, the thing "Keep it as it is" keeps.
                      currentValueFor={(field) => {
                        const v = cardVerdictOf(field);
                        return typeof v?.value === "string" && v.value.trim() !== ""
                          ? v.value
                          : null;
                      }}
                    />
                  )}
                {/* `refineStarted` keeps the keyed latch from resurrecting a
                    prior visit's card before "Start refining" re-seeds. The
                    dead-session gate mirrors RefineFlow's fatal-screen
                    precedence: an expired OR denied session must not keep an
                    interactive card whose answers would queue into an outbox
                    that can never flush. */}
                {!isNew &&
                  refineStarted &&
                  !isSessionDead(wsSession.status) &&
                  readinessEntry && (
                  <div className="issue-page-readiness-pin">
                    <ReadinessCard
                      issueIid={readinessEntry.issueIid}
                      verdicts={readinessEntry.verdicts}
                      dor={readinessEntry.dor}
                      dod={readinessEntry.dod}
                      invest={readinessEntry.invest}
                      methodologySkipped={readinessEntry.methodologySkipped}
                      allGreen={readinessEntry.allGreen}
                      {...(readinessEntry.acceptToken !== undefined
                        ? { acceptToken: readinessEntry.acceptToken }
                        : {})}
                      // A refused apply, on the surface it was refused on.
                      // Live #119: a second "Accept changes" here was refused
                      // with `card_already_applied`, the GitLab body did not
                      // move, and this card went on reading "Ready to start /
                      // Story quality 100/100" with Accept still enabled — the
                      // only account of the failure was one sentence of agent
                      // prose in the chat rail.
                      {...(wsSession.applyResult !== null &&
                      wsSession.applyResult.issueIid ===
                        readinessEntry.issueIid &&
                      !wsSession.applyResult.ok &&
                      wsSession.applyResult.error !== undefined
                        ? {
                            applyFailure: {
                              error: wsSession.applyResult.error,
                            },
                          }
                        : {})}
                      projectId={projectId}
                      // HELD, then sent as one `question_answer` frame bound to
                      // THIS issue (see `onFlushHeld`). The refine seed opens the
                      // flow with `{ issueIid }`, and once any turn in the log
                      // carries a binding the grounding gate reads only turns
                      // bound to the card's own issue (`propose-card.ts`) — the
                      // frame's echo carries it, so the user's words (typed, or
                      // Loom's taken in one click) stay visible to the gate.
                      onAnswer={(text, field) => answerRow(field, text)}
                      settled={redSettled}
                      held={heldByField}
                      sent={hold.sent}
                      onUndo={undoRow}
                      traceSurface="issue_card"
                      onAccept={() =>
                        wsSession.send(
                          buildReadinessAcceptMessage(
                            readinessEntry.issueIid,
                            readinessEntry.acceptToken,
                          ),
                        )
                      }
                      onDecline={(field) => {
                        wsSession.sendReadinessFieldDecline(
                          readinessEntry.issueIid,
                          field,
                        );
                      }}
                    />
                    {/* Split offer: the sizing step (auto-kicked at first
                        allGreen) sized this ticket. Parity with RefineFlow —
                        offer the split whether or not the policy recommended
                        one, so the hub and the flow expose the same choice for
                        the same issue. The proposal turn only runs on click. */}
                    {breakdownCard.sizing !== null &&
                      breakdownCard.entry === null &&
                      breakdownCard.phase === "none" &&
                      !breakdownCard.breakdownUnavailable &&
                      !bdDismissed &&
                      // Big but single-criterion: ask for the PIECES it ships as
                      // (the agent turns them into criteria, then it re-sizes and
                      // splits) — not "add acceptance criteria".
                      (breakdownCard.sizing.oversizedSingleCriterion ? (
                        <div className="issue-split-offer">
                          <span className="issue-split-offer-text">
                            {t("breakdown.split.oversizedSingle")}{" "}
                            <span className="fld-hint">
                              {breakdownCard.sizing.sizeSummary}
                            </span>
                          </span>
                          <span className="issue-split-offer-actions">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() =>
                                wsSession.send(buildPieceElicitationMessage())
                              }
                            >
                              {t("breakdown.split.breakIntoPieces")}
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setBdDismissed(true)}
                            >
                              {t("breakdown.decline")}
                            </button>
                          </span>
                        </div>
                      ) : (
                        <div className="issue-split-offer">
                          <span className="issue-split-offer-text">
                            {breakdownCard.sizing.splitRecommended
                              ? t("breakdown.split.recommended")
                              : t("breakdown.split.notNeeded")}{" "}
                            <span className="fld-hint">
                              {breakdownCard.sizing.sizeSummary}
                            </span>
                          </span>
                          {bdRequested ? (
                            <span className="fld-hint">
                              {t("breakdown.review.wait")}
                            </span>
                          ) : (
                            <span className="issue-split-offer-actions">
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => {
                                  breakdownCard.requestBreakdown(
                                    breakdownCard.sizing?.splitRecommended !== true,
                                  );
                                  setBdRequested(true);
                                }}
                              >
                                {breakdownCard.sizing.splitRecommended
                                  ? t("breakdown.split.cta")
                                  : t("breakdown.split.force")}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setBdDismissed(true)}
                              >
                                {t("breakdown.decline")}
                              </button>
                            </span>
                          )}
                        </div>
                      ))}
                    {(breakdownCard.phase !== "none" || breakdownCard.entry !== null) &&
                      !bdDismissed && (
                        <BreakdownCard
                          card={breakdownCard}
                          epicTitle={draft.title || issue?.title || ""}
                          issueIid={readinessEntry.issueIid}
                          {...(readinessEntry.acceptToken !== undefined
                            ? { acceptToken: readinessEntry.acceptToken }
                            : {})}
                          // The SAME fact the spread above reads. Without it
                          // the card re-derives "the token was stripped on
                          // purpose" from the token's mere absence, which a
                          // proposal that never had one also looks like.
                          confirmPending={
                            readinessEntry.acceptToken === undefined
                          }
                          agentBusy={wsSession.agentBusy}
                          // A session nothing will answer again disables every
                          // mutating control and says so, instead of leaving a
                          // live-looking card whose Confirm is a silent no-op.
                          // The idle hard-kill is included: this host renders
                          // the card under the same dialog the focus flow does.
                          sessionClosed={
                            wsSession.status === "idle_killed" ||
                            isSessionDead(wsSession.status)
                          }
                          // Once the readiness card is applied the issue
                          // description is on GitLab and there is no post-apply
                          // must-have write lane, so "don't build it" is
                          // refused server-side. Say so on the surface instead
                          // of letting the user pick it and get denied.
                          // `applyResult` is a single "last apply" slot, so it
                          // reads false for an already-applied issue that simply
                          // was not the most recent one. The durable signal is
                          // the card itself: an applied card is the one whose
                          // breakdown has moved past `proposed`, or whose accept
                          // token is spent. Falling back to the apply slot keeps
                          // the immediate post-apply render correct.
                          parkDisabled={
                            breakdownCard.phase === "creating" ||
                            breakdownCard.phase === "done" ||
                            (wsSession.applyResult?.issueIid ===
                              readinessEntry.issueIid &&
                              wsSession.applyResult.ok)
                          }
                          onSend={(text) =>
                            wsSession.send(text, {
                              flow: { issueIid: readinessEntry.issueIid },
                            })
                          }
                          onConfirm={() => {
                            if (readinessEntry.acceptToken !== undefined) {
                              breakdownCard.acceptBreakdown(
                                readinessEntry.acceptToken,
                              );
                            }
                          }}
                          // The card no longer sends the keep-single sentence
                          // itself — the lane it takes is the host's call (the
                          // focus flow has a screen where it must ride an open
                          // ask's frame). Here it is the plain self-labelled
                          // turn the card used to send, unchanged.
                          onDecline={() => {
                            wsSession.send(
                              buildKeepSingleMessage(readinessEntry.issueIid),
                              { flow: { issueIid: readinessEntry.issueIid } },
                            );
                            setBdDismissed(true);
                          }}
                          onOpenChild={onNavigateToIssue}
                          stateKey={`${session.sessionId ?? ""}:${iid ?? ""}:${wsSession.resetEpoch}`}
                          collapseOnDone
                        />
                      )}
                  </div>
                )}
                <section className="issue-page-section">
                  <label className="fld">
                    <span className="fld-label">{t("dashboard.issuePage.fieldTitle")}</span>
                    <AutoGrowTextarea
                      className="fld-input"
                      value={draft.title}
                      onChange={(e) => setField("title", e.target.value)}
                      placeholder={t("dashboard.issuePage.titlePlaceholder")}
                    />
                  </label>

                  <div className="fld">
                    <span className="fld-label">
                      {t("dashboard.issuePage.description")}{" "}
                      <span className="fld-hint">{t("dashboard.issuePage.hintMarkdown")}</span>
                    </span>
                    <MarkdownEditor
                      value={draft.description}
                      onChange={(next) => setField("description", next)}
                      placeholder={t("dashboard.issuePage.descriptionPlaceholder")}
                      height={360}
                      ariaLabel={t("dashboard.issuePage.descriptionAria")}
                    />
                  </div>

                  <label className="fld">
                    <span className="fld-label">
                      {t("dashboard.issuePage.businessOutcome")}{" "}
                      <span className="fld-hint">{t("dashboard.issuePage.hintWhyMatters")}</span>
                    </span>
                    <AutoGrowTextarea
                      className="fld-input"
                      value={draft.outcome}
                      onChange={(e) => setField("outcome", e.target.value)}
                      placeholder={t("dashboard.issuePage.outcomePlaceholder")}
                    />
                  </label>

                  <div className="fld">
                    <span className="fld-label">
                      {t("dashboard.issuePage.acceptanceCriteria")}{" "}
                      <span className="fld-hint">{t("dashboard.issuePage.hintHowDone")}</span>
                    </span>
                    <div className="ac-list">
                      {draft.acceptanceCriteria.map((a, i) => (
                        <div key={i} className="ac-item">
                          <span className="ac-bullet">{i + 1}</span>
                          <AutoGrowTextarea
                            className="ac-input"
                            value={a}
                            onChange={(e) => setAcAt(i, e.target.value)}
                            placeholder={t("dashboard.issuePage.acPlaceholder")}
                          />
                          {draft.acceptanceCriteria.length > 1 && (
                            <button
                              type="button"
                              className="ac-remove"
                              onClick={() => removeAc(i)}
                              title={t("common.remove")}
                            >
                              <Icon name="x" size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="ac-add" onClick={addAc}>
                      <Icon name="plus" size={12} /> {t("dashboard.issuePage.addCriterion")}
                    </button>
                  </div>

                  <div className="fld">
                    <span className="fld-label">{t("dashboard.issuePage.priority")}</span>
                    <div className="prio-seg">
                      {(Object.entries(PRIORITY_META) as Array<
                        [Priority, (typeof PRIORITY_META)[Priority]]
                      >).map(([k, v]) => (
                        <button
                          key={k}
                          type="button"
                          className={`prio-opt ${draft.priority === k ? "on" : ""}`}
                          onClick={() => setField("priority", k)}
                        >
                          <span
                            className="prio-dot"
                            style={{ background: v.color }}
                          />
                          {t(v.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isNew && (
                    <div className="dor">
                      <div className="dor-top">
                        <span className={`dor-state ${dorReady ? "ok" : ""}`}>
                          <Icon name={dorReady ? "check" : "spark"} size={12} />
                          {dorReady
                            ? t("dashboard.issuePage.readyToBuild")
                            : t("dashboard.issuePage.readinessPct", { pct: dorPct })}
                        </span>
                        <span className="dor-sub">
                          {dorReady
                            ? t("dashboard.issuePage.meetsDor")
                            : t("dashboard.issuePage.checksProgress", {
                                passed: dorPassed,
                                total: dorChecks.length,
                              })}
                        </span>
                      </div>
                      <div className="dor-bar">
                        <div
                          className="dor-fill"
                          style={{
                            width: `${dorPct}%`,
                            background: dorReady
                              ? "var(--gate-pass)"
                              : "var(--st-progress)",
                          }}
                        />
                      </div>
                      <div className="dor-checks">
                        {dorChecks.map((c, i) => (
                          <span
                            key={i}
                            className={`dor-check ${c.ok ? "ok" : ""}`}
                          >
                            <Icon name={c.ok ? "check" : "open"} size={11} />
                            {t(c.labelKey)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                {detail.data && (
                  <IssueLinksPanel
                    links={detail.data.links}
                    projectId={projectId}
                    onNavigate={onNavigateToIssue}
                    {...(detail.data.epicChildIids !== undefined
                      ? { childIids: detail.data.epicChildIids }
                      : {})}
                  />
                )}

                {detail.data && (
                  <LinkedMrsPanel mergeRequests={detail.data.mergeRequests} />
                )}
              </div>

              <aside className="issue-page-side">
                {isNew ? (
                  <section className="issue-page-section">
                    <h3 className="issue-panel-h">{t("dashboard.issuePage.refineWithLoom")}</h3>
                    <p className="muted">
                      {t("dashboard.issuePage.refineCreateFirst")}
                    </p>
                  </section>
                ) : (
                  <>
                    <div className="issue-page-tabs" role="tablist">
                      <button
                        role="tab"
                        aria-selected={side === "refine"}
                        className={`issue-page-tab ${side === "refine" ? "on" : ""}`}
                        onClick={() => setSide("refine")}
                      >
                        <Icon name="spark" size={12} /> {t("dashboard.issuePage.tab.refine")}
                      </button>
                      <button
                        role="tab"
                        aria-selected={side === "progression"}
                        className={`issue-page-tab ${side === "progression" ? "on" : ""}`}
                        onClick={() => setSide("progression")}
                      >
                        {t("dashboard.issuePage.tab.progression")}
                      </button>
                      <button
                        role="tab"
                        aria-selected={side === "activity"}
                        className={`issue-page-tab ${side === "activity" ? "on" : ""}`}
                        onClick={() => setSide("activity")}
                      >
                        {t("dashboard.issuePage.tab.activity")}
                      </button>
                    </div>

                    {side === "refine" &&
                      (refineStarted && session.sessionId !== null ? (
                        <div className="issue-page-rail">
                          <ChatPage
                            sessionId={session.sessionId}
                            onPickSession={() => {}}
                            onBackToPicker={onBackToPicker}
                            embedded
                            seed={refineSeed}
                            // Nothing in this seed is the user's: it is composed
                            // here from the iid and GitLab's own title (often
                            // Loom's earlier output). An empty span says so, and
                            // the gate grounds nothing off it.
                            seedEvidence=""
                            onSeedConsumed={() => setRefineSeed(null)}
                            seedMode="send"
                            externalSession={wsSession}
                            // A rail message while answers are held carries
                            // them in the SAME frame — two frames would be two
                            // turns, the first killed by the second.
                            beforeSend={(text) =>
                              hold.count > 0 ? hold.flushNow(text) : false
                            }
                            suppressReadinessCard
                            projectId={projectId}
                            flowIssueIid={iid ?? undefined}
                          />
                        </div>
                      ) : (
                        <section className="issue-page-section">
                          <h3 className="issue-panel-h">{t("dashboard.issuePage.refineWithLoom")}</h3>
                          <p className="muted">
                            {t("dashboard.issuePage.refineIntro")}
                          </p>
                          {session.error && (
                            <div className="issue-page-dor-reason" role="alert">
                              <Icon name="flag" size={12} />
                              <span>{session.error}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={startRefine}
                            disabled={session.starting}
                          >
                            <Icon name="spark" size={12} />
                            {session.starting
                              ? t("dashboard.issuePage.startingLoom")
                              : t("dashboard.issuePage.startRefining")}
                          </button>
                        </section>
                      ))}
                    {side === "progression" && (
                      <section className="issue-page-section">
                        <h3 className="issue-panel-h">{t("dashboard.issuePage.loomProgression")}</h3>
                        <IssueTimeline
                          events={timeline.events}
                          loading={timeline.loading}
                        />
                      </section>
                    )}
                    {side === "activity" && (
                      <section className="issue-page-section">
                        <h3 className="issue-panel-h">{t("dashboard.issuePage.activity")}</h3>
                        <IssueActivityTimeline
                          events={activity.events}
                          loading={activity.loading}
                        />
                      </section>
                    )}
                  </>
                )}
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
    </RemountSafeStoreProvider>
  );
};
